import * as Page from '../../components/content/page-header';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Modes } from '../../types/page-modes';
import Upload from '../../assets/icons/upload.svg';
import Download from '../../assets/icons/download.svg';
import Export from '../../assets/icons/export.svg';
import Translate from '../../assets/icons/translate.svg';
import { Actions, Content, Dialog, Title } from '../../components/dialog/dialog';
import { Select2 } from '../../components/forms/select';
import { Checkbox2 } from '../../components/forms/checkbox';
import * as Button from '../../components/button/button';
import { Icons } from '../../components/icons/icons';
import { Language } from './types/language';
import usePageState from '../../hooks/use-page-state';
import { PaginatedTable } from '../../components/table/paginated-table';
import { MainContent } from '../../components/layout/content';
import { Pageable, Query, usePagination } from '../../hooks/use-pagination';
import { useQuery } from '../../hooks/use-query';
import { Label } from '../../components/forms/input';
import { FieldValues, useForm } from 'react-hook-form';
import { useDeleteMutation, usePostMutation } from '../../hooks/use-mutations';
import { usePostFormDataMutation } from '../../hooks/use-form-mutations';
import { ActionColumn, CellImageRenderer } from '../../components/table/columns';
import { FileInputLarge } from '../../components/forms/file';
import { createJSONFile } from '../../utils/json-file';
import { alert } from '../../components/toast/toast';

type AvailableLanguage = {
  id: string;
  name: string;
};

export const Languages = (): JSX.Element => {
  const [pageState, setPageState] = usePageState();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const { register: registerUpload, handleSubmit: handleUploadSubmit, setValue, formState: { errors: uploadErrors } } = useForm();
  const [query, setQuery] = useState<Query>({});
  const { data } = usePagination<Pageable<Language>>('/languages', query);
  const { data: availableLanguages, isLoading } = useQuery<AvailableLanguage[]>({ path: '/languages/available' });
  const inputRef = useRef<HTMLInputElement>();
  const postMutation = usePostMutation({ path: '/languages', cacheKey: ['/languages', '/languages/available'] });
  const deleteMutation = useDeleteMutation({ cacheKey: ['/languages', '/languages/available'] });
  const downloadMutation = usePostMutation();
  const translateMutation = usePostMutation();
  const exportMutation = usePostMutation();
  const uploadFile = usePostFormDataMutation({ path: `/languages/upload/${pageState.item?.code as string}`, cacheKey: '/languages' });

  useEffect(() => {
    if (uploadFile.isSuccess) {
      setPageState(Modes.BROWSE);
      alert.error('File uploaded successfully');
    }
  }, [uploadFile.isSuccess]);

  const onSubmit = (values: FieldValues): void => {
    postMutation.mutate(values);
    setPageState(Modes.BROWSE);
  };

  const onDeleteConfirm = (): void => {
    deleteMutation.mutate({ path: `/languages/${pageState.item?.id as string}` });
    setPageState(Modes.BROWSE);
  };

  const onDownload = async (language: string): Promise<void> => {
    try {
      const result = await downloadMutation.mutateAsync({ path: `/languages/download/${language}` });
      createJSONFile(language, JSON.stringify(result));
    } catch(exception) {
      console.error(exception);
      alert.error('Download failed');
    }
  };

  const onTranslate = async (language: Language): Promise<void> => {
    try {
      await translateMutation.mutateAsync({ path: `/languages/translate/${language.code}` });
      alert.success(`Translation for ${language.name} is initialized`);
    } catch {
      alert.error(`Translation for ${language.name} failed to initialize`);
    }
  };

  const onUploadSubmit = (values: FieldValues): void => {
    uploadFile.mutate(values);
  };

  const onUpload = (language: Language): void => {
    setPageState(Modes.UPLOAD, language);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.value = '';
        inputRef.current.click();
      }
    }, 0);
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setValue('file', event.target.files);
  };

  const onExport = async (language: string): Promise<void> => {
    try {
      const result = await exportMutation.mutateAsync({ path: `/languages/export/${language}` });
      createJSONFile(language, JSON.stringify(result));
    } catch {
      alert.error('Export failed');
    }
  };

  const columns = [
    { accessor: 'name', Header: 'Name', sort: true },
    { accessor: 'fileName', Header: 'Info' },
    { accessor: 'upload', Header: 'Upload', Cell: (item: any): JSX.Element => {
      return <CellImageRenderer image={Upload} onClick={(): void => onUpload(item.row.original as Language)} />;
    } },
    { accessor: 'download', Header: 'Download', Cell: (item: any): JSX.Element => {
      return <CellImageRenderer image={Download} onClick={(): Promise<void> => onDownload(item.row.original.code as string)} />;
    } },
    { accessor: 'translate', Header: 'Translate', Cell: (item: any): JSX.Element => {
      return <CellImageRenderer image={Translate} onClick={(): Promise<void> => onTranslate(item.row.original as Language)} />;
    } },
    { accessor: 'export', Header: 'Export Human Readable', Cell: (item: any): JSX.Element => {
      return <CellImageRenderer image={Export} onClick={(): Promise<void> => onExport(item.row.original.code as string)} />;
    } },
    ActionColumn({ onDelete: (item: Language): void => setPageState(Modes.DELETE, item) })
  ];

  return (
    <>
      <Page.Header>
        <Page.Tenant />
        <Page.Title>
          Languages
          <Button.PrimarySmall onClick={(): void => setPageState(Modes.CREATE)}>
            <Icons.Add /> Add Language
          </Button.PrimarySmall>
        </Page.Title>
        <Page.SubTitle>Languages (locales) available within RAPIDS.</Page.SubTitle>
      </Page.Header>
      <MainContent>
        <PaginatedTable isLoading={isLoading} columns={columns} data={data} onChange={setQuery} />
        {Modes.CREATE === pageState.mode && (
          <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Title>Add Language</Title>
              <Content>
                <div className="mb-4">
                  <Label>Language</Label>
                  <Select2 {...register('id', { required: 'You must select a value for Language' })} errors={errors}>
                    <option value="">Select Language</option>
                    {availableLanguages?.map(language => {
                      return <option key={language.id} value={language.id}>{language.name}</option>;
                    })}
                  </Select2>
                </div>
                <div className="mb-4">
                  <Checkbox2 {...register('translateImmediately')} label="Translate Immediately" />
                </div>
              </Content>
              <Actions>
                <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
                <Button.Primary type="submit">
                  <Icons.Add /> Add Language
                </Button.Primary>
              </Actions>
            </form>
          </Dialog>
        )}

        {Modes.DELETE === pageState.mode && (
          <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
            <Title>Delete Language</Title>
            <Content>Are you sure you want to delete language?</Content>
            <Actions>
              <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)}>Cancel</Button.Tertiary>
              <Button.Danger onClick={onDeleteConfirm}>Delete</Button.Danger>
            </Actions>
          </Dialog>
        )}
        {Modes.UPLOAD === pageState.mode && (
          <Dialog onCancel={(): void => setPageState(Modes.BROWSE)} backdropClose={false}>
            <form onSubmit={handleUploadSubmit(onUploadSubmit)}>
              <Title>Upload Language</Title>
              <Content>
                <div className="mb-4">
                  <Label>Language File</Label>
                  <FileInputLarge { ...registerUpload('file', { required: 'You must select a file for Language File' })} errors={uploadErrors} accept="application/json" ref={inputRef} onChange={onFileChange} />
                  <span className="supported-file-format">Supported file format: JSON</span>
                </div>
              </Content>
              <Actions>
                <Button.Tertiary onClick={(): void => setPageState(Modes.BROWSE)} disabled={uploadFile.isLoading}>Cancel</Button.Tertiary>
                <Button.Primary type="submit" disabled={uploadFile.isLoading}>Add Language</Button.Primary>
              </Actions>
            </form>
          </Dialog>
        )}
      </MainContent>
    </>
  );
};
